/**
 * Page /admin/parametres-mairie — édition du profil tenant (L13.7).
 *
 * Accessible aux super_admin et admin (la permission `admin.tenant.update`
 * est sémée pour ces rôles en migration 002). La garde finale est faite
 * côté backend (403 sinon).
 *
 * Champs éditables (issus du schéma tenants migration 001) :
 *   - Identité : nom (requis), siret (14 chiffres)
 *   - Coordonnées : adresse, code postal (5 chiffres), ville, téléphone, email
 *
 * Champs lus mais NON éditables :
 *   - slug (URL identifiant, ne change pas après création)
 *   - codeInsee (identifiant INSEE de la commune, géré ailleurs)
 *   - type (commune/epci/syndicat, immutable)
 *   - pays (FR fixé)
 *
 * Pattern :
 *   - Chargement initial GET /api/me/tenant
 *   - Form contrôlé local, dirty state pour activer/désactiver le bouton
 *   - PUT /api/admin/tenant à la soumission
 *   - Feedback succès / erreur avec messages clairs
 *   - Validation côté client + côté serveur (Zod)
 */

import { useState, useEffect, useCallback, useRef, type FormEvent } from 'react';
import {
  Card, Button, Input, Field, Alert, LogoMark, SectionEyebrow, Badge,
} from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import type { IdentiteMairie, FondEcran } from '@/lib/api';
import { compressImageIfNeeded } from '@/lib/image-compress';
import { invaliderFondCommune } from '@/components/FondCommune';
import {
  Landmark, Save, AlertCircle, CheckCircle2, Building2, MapPin, Phone, Mail, FileText,
  Image as ImageIcon, Upload, Trash2, CreditCard, ReceiptText, CalendarDays, Users,
} from 'lucide-react';
import type { TenantDetail, ReglagePorteeDiffusion } from '@roronoa-collectivites/shared-types';
import { PassationMandatCard } from '@/components/admin/PassationMandatCard';
import { RepriseMairePanel } from '@/components/admin/gouvernance/RepriseMairePanel';
import { Link } from 'react-router-dom';

interface FormState {
  nom: string;
  codeInsee: string;
  siret: string;
  adresse: string;
  codePostal: string;
  ville: string;
  telephone: string;
  emailContact: string;
  zoneVacances: string;
  tiersTeletransmission: string;
  lienPrefectureActes: string;
  rappelLoiTexte: string;
}

const EMPTY_FORM: FormState = {
  nom: '',
  codeInsee: '',
  siret: '',
  adresse: '',
  codePostal: '',
  ville: '',
  telephone: '',
  emailContact: '',
  zoneVacances: '',
  tiersTeletransmission: '',
  lienPrefectureActes: '',
  rappelLoiTexte: '',
};

function tenantToForm(t: TenantDetail): FormState {
  return {
    nom: t.nom ?? '',
    codeInsee: t.codeInsee ?? '',
    siret: t.siret ?? '',
    adresse: t.adresse ?? '',
    codePostal: t.codePostal ?? '',
    ville: t.ville ?? '',
    telephone: t.telephone ?? '',
    emailContact: t.emailContact ?? '',
    zoneVacances: t.zoneVacances ?? '',
    tiersTeletransmission: t.tiersTeletransmission ?? '',
    lienPrefectureActes: t.lienPrefectureActes ?? '',
    rappelLoiTexte: t.rappelLoiTexte ?? '',
  };
}

export function TenantSettingsPage() {
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const t = await api.admin.tenant.get();
      setTenant(t);
      setForm(tenantToForm(t));
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function updateField(key: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => {
      const { [key]: _omit, ...rest } = prev;
      return rest;
    });
    setSuccess(null);
  }

  const dirty = tenant
    ? Object.entries(form).some(([k, v]) => {
        const original = (tenantToForm(tenant) as unknown as Record<string, string>)[k] ?? '';
        return v !== original;
      })
    : false;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!dirty || saving) return;

    setSaving(true);
    setError(null);
    setSuccess(null);
    setFieldErrors({});

    try {
      const updated = await api.admin.tenant.update({
        nom: form.nom.trim(),
        codeInsee: form.codeInsee.trim() || null,
        siret: form.siret.trim() || null,
        adresse: form.adresse.trim() || null,
        codePostal: form.codePostal.trim() || null,
        ville: form.ville.trim() || null,
        telephone: form.telephone.trim() || null,
        emailContact: form.emailContact.trim() || null,
        zoneVacances: (form.zoneVacances || null) as 'A' | 'B' | 'C' | null,
        tiersTeletransmission: form.tiersTeletransmission.trim() || null,
        lienPrefectureActes: form.lienPrefectureActes.trim() || null,
        rappelLoiTexte: form.rappelLoiTexte.trim() || null,
      });

      setTenant(updated);
      setForm(tenantToForm(updated));
      setSuccess('Modifications enregistrées avec succès.');

      // Disparition automatique du toast succès après 4s
      window.setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      if (err instanceof ApiException) {
        // VALIDATION_ERROR backend → mapper les fieldErrors si dispo
        const details = err.payload.details as { fieldErrors?: Record<string, string[]> } | undefined;
        if (details?.fieldErrors) {
          const mapped: Record<string, string> = {};
          for (const [k, msgs] of Object.entries(details.fieldErrors)) {
            if (Array.isArray(msgs) && msgs.length > 0 && typeof msgs[0] === 'string') {
              mapped[k] = msgs[0];
            }
          }
          setFieldErrors(mapped);
        }
        setError(err.payload.error);
      } else {
        setError('Erreur inconnue');
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Card variant="glass" padded>
        <div className="flex items-center gap-3 text-ink-muted">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-tech border-t-transparent" aria-hidden="true" />
          <span className="text-sm">Chargement du profil…</span>
        </div>
      </Card>
    );
  }

  if (error && !tenant) {
    return (
      <Alert variant="error" icon={AlertCircle}>
        {error}
      </Alert>
    );
  }

  return (
    <div className="space-y-5">
      {/* ===== En-tête de page ===== */}
      <div className="flex items-center gap-3">
        <LogoMark icon={Landmark} accent="tech" size="lg" />
        <div>
          <SectionEyebrow as="div">Administration</SectionEyebrow>
          <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
            Paramètres de la mairie
          </h1>
        </div>
      </div>

      {/* ===== Bannière succès / erreur ===== */}
      {success && (
        <Alert variant="success" icon={CheckCircle2}>
          {success}
        </Alert>
      )}
      {error && tenant && (
        <Alert variant="error" icon={AlertCircle}>
          {error}
        </Alert>
      )}

      {/* ===== Infos non éditables (en lecture seule) ===== */}
      {tenant && (
        <Card variant="glass" padded>
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="neutral" size="sm">
              <FileText className="h-3 w-3" aria-hidden="true" />
              Informations système
            </Badge>
            <span className="text-2xs text-ink-subtle">non éditables ici</span>
          </div>
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Slug</dt>
              <dd className="mt-0.5 font-mono text-ink">{tenant.slug}</dd>
            </div>
            <div>
              <dt className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Code INSEE</dt>
              <dd className="mt-0.5 font-mono text-ink">{tenant.codeInsee ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Type</dt>
              <dd className="mt-0.5 text-ink capitalize">{tenant.type}</dd>
            </div>
            <div>
              <dt className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Pays</dt>
              <dd className="mt-0.5 text-ink">{tenant.pays}</dd>
            </div>
          </dl>
        </Card>
      )}

      {/* ===== Formulaire d'édition ===== */}
      <form onSubmit={onSubmit} noValidate>
        {/* SECTION 1 : Identité administrative */}
        <Card variant="glass" padded>
          <div className="flex items-center gap-2 mb-4">
            <div
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-lg bg-tech-tint/70 text-tech-fg"
              aria-hidden="true"
            >
              <Building2 className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-display text-base font-semibold text-ink">Identité</h2>
              <p className="text-2xs text-ink-subtle">Dénomination officielle de la collectivité</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Field label="Nom de la collectivité" htmlFor="tenant-nom" required error={fieldErrors.nom}>
                <Input
                  id="tenant-nom"
                  type="text"
                  value={form.nom}
                  onChange={(e) => updateField('nom', e.target.value)}
                  placeholder="Mairie de…"
                  required
                  aria-invalid={!!fieldErrors.nom}
                />
              </Field>
            </div>

            <div>
              <Field
                label="SIRET"
                htmlFor="tenant-siret"
                error={fieldErrors.siret}
                hint="14 chiffres, sans espaces ni tirets"
              >
                <Input
                  id="tenant-siret"
                  type="text"
                  inputMode="numeric"
                  value={form.siret}
                  onChange={(e) => updateField('siret', e.target.value)}
                  placeholder="12345678901234"
                  maxLength={14}
                  aria-invalid={!!fieldErrors.siret}
                />
              </Field>
            </div>

            <div>
              <Field
                label="Code INSEE de la commune"
                htmlFor="tenant-insee"
                error={fieldErrors.codeInsee}
                hint="5 caractères — sert à numéroter les dossiers d'urbanisme"
              >
                <Input
                  id="tenant-insee"
                  type="text"
                  value={form.codeInsee}
                  onChange={(e) => updateField('codeInsee', e.target.value.toUpperCase())}
                  placeholder="17333"
                  maxLength={5}
                  aria-invalid={!!fieldErrors.codeInsee}
                />
              </Field>
            </div>
          </div>
        </Card>

        {/* SECTION 2 : Adresse */}
        {/* M8 — photo obligatoire ou non pour les habitants. */}
        {/* M21 — passation de mandat du maire (élections 2026). */}
        <PassationMandatCard />
        {/* Refonte UX, chantier 4 — le droit de reprise du maire (CGCT L2122-18). */}
        <RepriseMairePanel />

        <Card variant="glass" padded className="mt-4">
          <div className="flex items-center gap-2 mb-4">
            <div
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-lg bg-tech-tint/70 text-tech-fg"
              aria-hidden="true"
            >
              <MapPin className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-display text-base font-semibold text-ink">Adresse</h2>
              <p className="text-2xs text-ink-subtle">Siège de la collectivité</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Field label="Adresse" htmlFor="tenant-adresse" error={fieldErrors.adresse}>
                <Input
                  id="tenant-adresse"
                  type="text"
                  value={form.adresse}
                  onChange={(e) => updateField('adresse', e.target.value)}
                  placeholder="1 place de la Mairie"
                  aria-invalid={!!fieldErrors.adresse}
                />
              </Field>
            </div>

            <Field
              label="Code postal"
              htmlFor="tenant-codepostal"
              error={fieldErrors.codePostal}
              hint="5 chiffres"
            >
              <Input
                id="tenant-codepostal"
                type="text"
                inputMode="numeric"
                value={form.codePostal}
                onChange={(e) => updateField('codePostal', e.target.value)}
                placeholder="17780"
                maxLength={5}
                aria-invalid={!!fieldErrors.codePostal}
              />
            </Field>

            <Field label="Ville" htmlFor="tenant-ville" error={fieldErrors.ville}>
              <Input
                id="tenant-ville"
                type="text"
                value={form.ville}
                onChange={(e) => updateField('ville', e.target.value)}
                placeholder="Saint-Nazaire-sur-Charente"
                aria-invalid={!!fieldErrors.ville}
              />
            </Field>
          </div>
        </Card>

        {/* SECTION 3 : Contact */}
        <Card variant="glass" padded className="mt-4">
          <div className="flex items-center gap-2 mb-4">
            <div
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-lg bg-tech-tint/70 text-tech-fg"
              aria-hidden="true"
            >
              <Phone className="h-4 w-4" />
            </div>
            <div>
              <h2 className="font-display text-base font-semibold text-ink">Contact</h2>
              <p className="text-2xs text-ink-subtle">Coordonnées affichées aux administrés</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Téléphone"
              htmlFor="tenant-tel"
              error={fieldErrors.telephone}
              hint="Format libre : 05 46 88 12 34 ou +33 5 46 88 12 34"
            >
              <Input
                id="tenant-tel"
                type="tel"
                value={form.telephone}
                onChange={(e) => updateField('telephone', e.target.value)}
                placeholder="05 46 88 12 34"
                aria-invalid={!!fieldErrors.telephone}
              />
            </Field>

            <Field
              label="Email de contact"
              htmlFor="tenant-email"
              error={fieldErrors.emailContact}
            >
              <Input
                id="tenant-email"
                type="email"
                value={form.emailContact}
                onChange={(e) => updateField('emailContact', e.target.value)}
                placeholder="contact@mairie.fr"
                aria-invalid={!!fieldErrors.emailContact}
              />
            </Field>
          </div>
        </Card>

        {/* ===== Services scolaires ===== */}
        <Card className="mt-5">
          <div className="mb-4">
            <h2 className="font-display text-base font-semibold text-ink">Services scolaires</h2>
            <p className="text-2xs text-ink-subtle">Utilisé pour le calendrier des plannings (vacances scolaires)</p>
          </div>
          <Field
            label="Zone de vacances scolaires"
            htmlFor="tenant-zone"
            hint="Détermine les périodes de vacances grisées sur les plannings. Charente-Maritime = zone A."
          >
            <select
              id="tenant-zone"
              value={form.zoneVacances}
              onChange={(e) => updateField('zoneVacances', e.target.value)}
              className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-bg"
            >
              <option value="">— Non renseignée —</option>
              <option value="A">Zone A</option>
              <option value="B">Zone B</option>
              <option value="C">Zone C</option>
            </select>
          </Field>

          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-2xs font-semibold uppercase tracking-wide text-ink-subtle mb-3">Contrôle de légalité (@ACTES)</p>
            <Field
              label="Opérateur de télétransmission homologué"
              htmlFor="tenant-tiers"
              hint="Choisi par la commune parmi la liste des opérateurs homologués par le ministère de l'Intérieur. Sert à personnaliser le guide de transmission des arrêtés d'urbanisme."
            >
              <select
                id="tenant-tiers"
                value={form.tiersTeletransmission}
                onChange={(e) => updateField('tiersTeletransmission', e.target.value)}
                className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-bg"
              >
                <option value="">— Non défini —</option>
                <option value="Berger-Levrault (BL-Échanges)">Berger-Levrault (BL-Échanges)</option>
                <option value="Docapost (FAST-Actes)">Docapost (FAST-Actes)</option>
                <option value="JVS-Mairistem">JVS-Mairistem</option>
                <option value="Dematis (S²LOW)">Dematis (S²LOW)</option>
                <option value="Autre opérateur homologué">Autre opérateur homologué</option>
              </select>
            </Field>
            <Field
              label="Lien vers la page « télétransmission des actes » de votre préfecture (optionnel)"
              htmlFor="tenant-lien-actes"
              error={fieldErrors.lienPrefectureActes}
              hint="Affiché comme raccourci sur les dossiers d'urbanisme. Laissez vide si vous préférez le guide générique."
            >
              <Input
                id="tenant-lien-actes"
                type="url"
                placeholder="https://www.charente-maritime.gouv.fr/…"
                value={form.lienPrefectureActes}
                onChange={(e) => updateField('lienPrefectureActes', e.target.value)}
                aria-invalid={!!fieldErrors.lienPrefectureActes}
              />
            </Field>
          </div>
        </Card>

        {/* ===== Rappel à la loi — item 2.4 du retour mairie ===== */}
        {/* C'est ce qui reste de l'arbitrage D1 : la modération IA a été écartée
            (un-contre-un ≠ diffusion de masse), le rappel est « la seule mesure
            retenue ». Section à part : ce réglage n'a rien à voir avec
            l'urbanisme au-dessus — il concerne toutes les briques ouvertes aux
            administrés. */}
        <Card variant="glass" padded className="mt-5">
          <SectionEyebrow>Rappel à la loi</SectionEyebrow>
          <p className="mt-2 text-xs text-ink-muted leading-relaxed">
            Ce texte s'affiche sous les champs de saisie ouverts aux administrés
            (signalements, messages, envoi d'images). Il rappelle les règles —
            il ne bloque ni ne filtre aucun envoi.
          </p>
          <div className="mt-4">
            <Field
              label="Texte du rappel (optionnel)"
              htmlFor="tenant-rappel-loi"
              error={fieldErrors.rappelLoiTexte}
              hint="Laissez vide pour n'afficher aucun bandeau. Ce texte engage votre commune : rédigez-le avec vos mots."
            >
              <textarea
                id="tenant-rappel-loi"
                rows={3}
                maxLength={1000}
                placeholder="Ex. : Les propos injurieux, diffamatoires ou discriminatoires sont passibles de poursuites. Les photos envoyées ne doivent pas permettre d'identifier une personne sans son accord."
                value={form.rappelLoiTexte}
                onChange={(e) => updateField('rappelLoiTexte', e.target.value)}
                aria-invalid={!!fieldErrors.rappelLoiTexte}
                className="w-full rounded-r-md border border-border bg-surface/60 px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[oklch(var(--c-tech))] aria-[invalid=true]:border-urgent"
              />
            </Field>
            <p className="mt-1 text-2xs text-ink-subtle">
              {form.rappelLoiTexte.length}/1000 caractères
            </p>
          </div>
        </Card>

        {/* ===== Footer actions ===== */}
        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-2xs text-ink-subtle inline-flex items-center gap-1.5">
            <Mail className="h-3 w-3" aria-hidden="true" />
            Les coordonnées seront visibles par tous les administrés sur leur espace personnel.
          </p>
          <Button
            type="submit"
            variant="primary"
            disabled={!dirty || saving}
            aria-disabled={!dirty || saving}
          >
            <Save className="h-4 w-4" aria-hidden="true" />
            {saving ? 'Enregistrement…' : 'Enregistrer les modifications'}
          </Button>
        </div>
      </form>

      {/* ===== SECTION Identité mairie (logo / en-tête / pied) — migration 132 ===== */}
      <IdentiteMairieCard />

      {/* ===== SECTION Fond d'écran du tableau de bord (mig 294) ===== */}
      <FondEcranCard />

      {/* ===== SECTION Visibilité des plannings (migration 209) ===== */}
      <SignataireCard />

      {/* Refonte UX, chantier 1 — ces réglages vivent désormais dans leur service
          (même droit qu'avant : on a déplacé l'écran, pas la garde serveur). */}
      <Card variant="glass" padded className="mt-4">
        <h2 className="mb-2 text-sm font-semibold text-ink">Réglages déplacés dans les services</h2>
        <ul className="space-y-1.5 text-sm">
          <li><Link className="text-accent underline" to="/service-technique/reglages">Photo obligatoire des fiches incident</Link> <span className="text-ink-muted">— Service technique → Réglages</span></li>
          <li><Link className="text-accent underline" to="/rh?onglet=parametres">Visibilité des plannings pour les agents</Link> <span className="text-ink-muted">— Ressources humaines → Paramètres (roue dentée)</span></li>
          <li><Link className="text-accent underline" to="/grc/reglages">Portée des diffusions</Link> <span className="text-ink-muted">— Relation citoyens → Réglages</span></li>
          <li><Link className="text-accent underline" to="/comptabilite?onglet=reglages">PayFiP et facture électronique</Link> <span className="text-ink-muted">— Comptabilité → Réglages</span></li>
        </ul>
      </Card>
    </div>
  );
}

// ============================================================================
// Visibilité des plannings — « les agents voient-ils le planning du service ? »
// Réglage commune (migration 209). Coché par défaut (ouvert). Décocher
// cloisonne : chaque agent ne voit alors que ses propres créneaux.
// ============================================================================
/** Réutilisée dans l'onglet « Réglages » de sa brique (refonte UX, chantier 1). */
export function PlanningVisibiliteCard() {
  const [ouvert, setOuvert] = useState(true);
  const [initial, setInitial] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.admin.planningVisibilite.get();
      setOuvert(r.agentsVoientService);
      setInitial(r.agentsVoientService);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  const dirty = ouvert !== initial;

  async function enregistrer() {
    setSaving(true); setError(null); setSuccess(null);
    try {
      await api.admin.planningVisibilite.setGlobal(ouvert);
      setInitial(ouvert);
      setSuccess('Réglage enregistré.');
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible.');
    } finally { setSaving(false); }
  }

  return (
    <Card variant="glass" padded className="mt-4">
      <div className="mb-3 flex items-center gap-2">
        <CalendarDays className="h-5 w-5 text-tech" aria-hidden="true" />
        <h2 className="font-display text-base font-semibold text-ink">Visibilité des plannings</h2>
      </div>

      {loading ? (
        <p className="py-4 text-sm text-ink-subtle">Chargement…</p>
      ) : (
        <div className="space-y-4">
          {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
          {success && <Alert variant="success" icon={CheckCircle2}>{success}</Alert>}

          <p className="text-sm text-ink-muted">
            Par défaut, chaque agent voit le planning complet de son service (le sien et celui de ses collègues),
            en lecture seule. Cela vaut pour tous les services concernés : technique, scolaire, petite enfance,
            communication, urbanisme, comptabilité. Décochez pour cloisonner : chaque agent ne verra alors que
            ses propres créneaux. La modification des plannings reste réservée aux responsables dans tous les cas.
          </p>

          <label className="flex items-center gap-3 rounded-r-lg border border-border bg-surface/40 px-3 py-2.5">
            <input
              type="checkbox"
              checked={ouvert}
              onChange={(e) => setOuvert(e.target.checked)}
              className="h-4 w-4"
            />
            <span className="flex items-center gap-2 text-sm font-medium text-ink">
              <Users className="h-4 w-4 text-ink-muted" aria-hidden="true" />
              Les agents voient le planning complet de leur service
            </span>
            {ouvert
              ? <Badge variant="success" size="sm" className="ml-auto">ouvert</Badge>
              : <Badge variant="neutral" size="sm" className="ml-auto">cloisonné</Badge>}
          </label>

          <Button variant="primary" onClick={() => void enregistrer()} disabled={!dirty || saving} aria-disabled={!dirty || saving}>
            <Save className="h-4 w-4" aria-hidden="true" />
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// Identité mairie — logo, en-tête (bannière), pied de document (migration 132)
// ============================================================================
function IdentiteMairieCard() {
  const [identite, setIdentite] = useState<IdentiteMairie | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pied, setPied] = useState('');
  const [piedDirty, setPiedDirty] = useState(false);
  const [busy, setBusy] = useState<'logo' | 'entete' | 'pied' | null>(null);
  const logoRef = useRef<HTMLInputElement>(null);
  const enteteRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const id = await api.admin.tenant.identite.get();
      setIdentite(id); setPied(id.piedTexte ?? ''); setPiedDirty(false);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  function flash(msg: string) { setSuccess(msg); setTimeout(() => setSuccess(null), 2500); }

  async function upload(kind: 'logo' | 'entete', file: File) {
    setBusy(kind); setError(null);
    try {
      const compressed = await compressImageIfNeeded(file);
      const res = kind === 'logo'
        ? await api.admin.tenant.identite.uploadLogo(compressed, file.name)
        : await api.admin.tenant.identite.uploadEntete(compressed, file.name);
      setIdentite(res);
      flash(kind === 'logo' ? 'Logo mis à jour.' : 'En-tête mis à jour.');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Envoi impossible (PNG ou JPEG uniquement).'); }
    finally { setBusy(null); if (logoRef.current) logoRef.current.value = ''; if (enteteRef.current) enteteRef.current.value = ''; }
  }

  async function remove(kind: 'logo' | 'entete') {
    setBusy(kind); setError(null);
    try {
      const res = kind === 'logo'
        ? await api.admin.tenant.identite.removeLogo()
        : await api.admin.tenant.identite.removeEntete();
      setIdentite(res);
      flash('Image supprimée.');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Suppression impossible'); }
    finally { setBusy(null); }
  }

  async function savePied() {
    setBusy('pied'); setError(null);
    try {
      const res = await api.admin.tenant.identite.setPied(pied.trim() || null);
      setIdentite(res); setPiedDirty(false);
      flash('Pied de document enregistré.');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Enregistrement impossible'); }
    finally { setBusy(null); }
  }

  return (
    <Card variant="glass" padded className="mt-4">
      <div className="flex items-center gap-2 mb-4">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-lg bg-grc-tint/70 text-grc-fg" aria-hidden="true">
          <ImageIcon className="h-4 w-4" />
        </div>
        <div>
          <h2 className="font-display text-base font-semibold text-ink">Identité & logo</h2>
          <p className="text-2xs text-ink-subtle">Logo et en-tête repris sur les documents officiels (PDF, courriers) et les emails.</p>
        </div>
      </div>

      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}
      {success && <Alert variant="success" icon={CheckCircle2} className="mb-3">{success}</Alert>}

      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : (
        <div className="space-y-5">
          {/* Logo */}
          <div>
            <div className="mb-1.5 text-sm font-medium text-ink">Logo / blason</div>
            <p className="mb-2 text-2xs text-ink-subtle">Symbole carré ou rond de la commune. Utilisé en tête des PDF et des emails. PNG ou JPEG, 5 Mo max.</p>
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 flex-none items-center justify-center overflow-hidden rounded-r-lg border border-border bg-surface/50">
                {identite?.logoUrl
                  ? <img src={identite.logoUrl} alt="Logo de la commune" className="h-full w-full object-contain" />
                  : <ImageIcon className="h-7 w-7 text-ink-dim" />}
              </div>
              <div className="flex flex-wrap gap-2">
                <input ref={logoRef} type="file" accept="image/png,image/jpeg" className="hidden" aria-label="Choisir le logo de la commune"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload('logo', f); }} />
                <Button variant="secondary" size="sm" onClick={() => logoRef.current?.click()} disabled={busy === 'logo'}>
                  <Upload className="h-4 w-4" /> {busy === 'logo' ? 'Envoi…' : (identite?.logoUrl ? 'Remplacer' : 'Téléverser')}
                </Button>
                {identite?.logoUrl && (
                  <Button variant="ghost" size="sm" onClick={() => void remove('logo')} disabled={busy === 'logo'} className="text-ink-muted hover:text-urgent">
                    <Trash2 className="h-4 w-4" /> Retirer
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* En-tête (bannière large) */}
          <div className="border-t border-border pt-4">
            <div className="mb-1.5 text-sm font-medium text-ink">En-tête (bannière large)</div>
            <p className="mb-2 text-2xs text-ink-subtle">Bandeau pleine largeur, optionnel — alternative au logo pour un en-tête complet en haut des documents. PNG ou JPEG.</p>
            <div className="space-y-2">
              <div className="flex h-24 w-full items-center justify-center overflow-hidden rounded-r-lg border border-border bg-surface/50">
                {identite?.enteteUrl
                  ? <img src={identite.enteteUrl} alt="En-tête de la commune" className="max-h-full max-w-full object-contain" />
                  : <span className="text-2xs text-ink-dim">Aucune bannière</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                <input ref={enteteRef} type="file" accept="image/png,image/jpeg" className="hidden" aria-label="Choisir l’image d’en-tête des documents"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload('entete', f); }} />
                <Button variant="secondary" size="sm" onClick={() => enteteRef.current?.click()} disabled={busy === 'entete'}>
                  <Upload className="h-4 w-4" /> {busy === 'entete' ? 'Envoi…' : (identite?.enteteUrl ? 'Remplacer' : 'Téléverser')}
                </Button>
                {identite?.enteteUrl && (
                  <Button variant="ghost" size="sm" onClick={() => void remove('entete')} disabled={busy === 'entete'} className="text-ink-muted hover:text-urgent">
                    <Trash2 className="h-4 w-4" /> Retirer
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Pied de document */}
          <div className="border-t border-border pt-4">
            <Field label="Mention de pied de document" htmlFor="identite-pied">
              <textarea
                id="identite-pied" rows={2}
                className="w-full rounded-r-md border border-border bg-bg px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
                placeholder="Ex. Mairie de … — 6 place de la Mairie, 17000 … — Tél. 05 … — contact@…"
                value={pied}
                onChange={(e) => { setPied(e.target.value); setPiedDirty(true); }}
              />
            </Field>
            <p className="mt-1 text-2xs text-ink-subtle">Apparaît en bas des documents générés (facultatif). La facturation garde ses propres mentions.</p>
            <div className="mt-2 flex justify-end">
              <Button variant="primary" size="sm" onClick={savePied} disabled={!piedDirty || busy === 'pied'}>
                <Save className="h-4 w-4" /> {busy === 'pied' ? 'Enregistrement…' : 'Enregistrer le pied'}
              </Button>
            </div>
          </div>

          <p className="text-2xs text-ink-subtle inline-flex items-center gap-1.5">
            <FileText className="h-3 w-3" aria-hidden="true" />
            Le paramétrage de la facturation (RIB, délai de paiement) reste dans Cantine → Paramètres et n'est pas modifié ici.
          </p>
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// PayFiP + mode e-facture (migration 159)
// La commune garde la main : PayFiP est un moyen parmi d'autres. Désactivé →
// paiement manuel « marquer payé » (chèque/espèces/virement restent légaux).
// ============================================================================

/** Réutilisée dans l'onglet « Réglages » de sa brique (refonte UX, chantier 1). */
export function PayfipConfigCard() {
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [payfipActif, setPayfipActif] = useState(false);
  const [payfipNumcli, setPayfipNumcli] = useState('');
  const [payfipSecret, setPayfipSecret] = useState(''); // vide = inchangé
  const [modeEfacture, setModeEfacture] = useState<'manuel' | 'helios' | 'chorus_direct'>('manuel');
  // Identifiants e-facture par mode (secrets : vide = inchangé)
  const [chorusStructureId, setChorusStructureId] = useState('');
  const [chorusTechLogin, setChorusTechLogin] = useState('');
  const [chorusTechPassword, setChorusTechPassword] = useState('');
  const [heliosCode, setHeliosCode] = useState('');
  const [heliosDepotUrl, setHeliosDepotUrl] = useState('');
  const [heliosIdPoste, setHeliosIdPoste] = useState('');
  const [fcClientId, setFcClientId] = useState('');
  const [fcClientSecret, setFcClientSecret] = useState(''); // vide = inchangé
  const [apiParticulierToken, setApiParticulierToken] = useState(''); // vide = inchangé
  const [apiEntrepriseToken, setApiEntrepriseToken] = useState(''); // vide = inchangé

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const t = await api.admin.tenant.get();
      setTenant(t);
      setPayfipActif(t.payfipActif);
      setPayfipNumcli(t.payfipNumcli ?? '');
      setModeEfacture(t.modeEfacture);
      setChorusStructureId(t.chorusStructureId ?? '');
      setChorusTechLogin(t.chorusTechLogin ?? '');
      setHeliosCode(t.heliosCodeCollectivite ?? '');
      setHeliosDepotUrl(t.heliosDepotUrl ?? '');
      setHeliosIdPoste(t.heliosIdPoste ?? '');
      setFcClientId(t.fcClientId ?? '');
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  async function enregistrer() {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const updated = await api.admin.tenant.payfipConfig({
        payfipActif,
        payfipNumcli: payfipNumcli.trim() || null,
        // Envoi du secret seulement si l'utilisateur a saisi une valeur.
        payfipSecret: payfipSecret ? payfipSecret : null,
        modeEfacture,
        chorusStructureId: chorusStructureId.trim() || null,
        chorusTechLogin: chorusTechLogin.trim() || null,
        chorusTechPassword: chorusTechPassword ? chorusTechPassword : null,
        heliosCodeCollectivite: heliosCode.trim() || null,
        heliosDepotUrl: heliosDepotUrl.trim() || null,
        heliosIdPoste: heliosIdPoste.trim() || null,
        fcClientId: fcClientId.trim() || null,
        fcClientSecret: fcClientSecret ? fcClientSecret : null,
        apiParticulierToken: apiParticulierToken ? apiParticulierToken : null,
        apiEntrepriseToken: apiEntrepriseToken ? apiEntrepriseToken : null,
      });
      setTenant(updated);
      setPayfipSecret('');
      setChorusTechPassword('');
      setFcClientSecret('');
      setApiParticulierToken('');
      setApiEntrepriseToken('');
      setSuccess('Configuration enregistrée.');
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible.');
    } finally { setSaving(false); }
  }

  return (
    <Card variant="glass" padded className="mt-4">
      <div className="mb-3 flex items-center gap-2">
        <CreditCard className="h-5 w-5 text-compta" aria-hidden="true" />
        <h2 className="font-display text-base font-semibold text-ink">Paiement en ligne &amp; facture électronique</h2>
      </div>

      {loading ? (
        <p className="py-4 text-sm text-ink-subtle">Chargement…</p>
      ) : (
        <div className="space-y-4">
          {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
          {success && <Alert variant="success" icon={CheckCircle2}>{success}</Alert>}

          <p className="text-sm text-ink-muted">
            PayFiP (DGFiP) permet à vos administrés de régler en ligne par carte. C'est un moyen parmi d'autres :
            chèque, espèces et virement restent possibles. Tant que PayFiP est désactivé, vous encaissez « à la main »
            via « Marquer payée » dans la comptabilité.
          </p>

          <label className="flex items-center gap-3 rounded-r-lg border border-border bg-surface/40 px-3 py-2.5">
            <input type="checkbox" checked={payfipActif} onChange={(e) => setPayfipActif(e.target.checked)} className="h-4 w-4" />
            <span className="text-sm font-medium text-ink">Proposer le paiement en ligne PayFiP</span>
            {tenant?.payfipActif ? <Badge variant="success" size="sm" className="ml-auto">activé</Badge> : <Badge variant="neutral" size="sm" className="ml-auto">désactivé</Badge>}
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="payfip-numcli" className="mb-1 block text-xs font-medium text-ink-muted">Numéro client PayFiP (numcli de la régie)</label>
              <Input id="payfip-numcli" value={payfipNumcli} onChange={(e) => setPayfipNumcli(e.target.value)} placeholder="Ex. 012345" />
            </div>
            <div>
              <label htmlFor="payfip-secret" className="mb-1 block text-xs font-medium text-ink-muted">{`Secret de régie ${tenant?.payfipSecretConfigure ? '(déjà configuré)' : ''}`}</label>
              <Input id="payfip-secret" type="password" value={payfipSecret} onChange={(e) => setPayfipSecret(e.target.value)}
                placeholder={tenant?.payfipSecretConfigure ? '•••••••• (laisser vide pour conserver)' : 'Coller le secret DGFiP'} />
            </div>
          </div>
          <p className="text-xs text-ink-subtle">
            Sans numéro/secret configurés, PayFiP fonctionne en mode test (paiement simulé). Le secret n'est jamais réaffiché.
          </p>

          <div className="border-t border-border pt-3">
            <div className="mb-2 flex items-center gap-2">
              <ReceiptText className="h-4 w-4 text-compta" aria-hidden="true" />
              <h3 className="text-sm font-semibold text-ink">Mode facture électronique (clients professionnels)</h3>
            </div>
            <p className="mb-2 text-xs text-ink-muted">
              Pour les factures aux entreprises (obligation e-facture 2026). Choisissez selon votre gestion en trésorerie.
            </p>
            <div className="space-y-2">
              {([
                { k: 'manuel', label: 'Manuel', desc: 'PDF / Excel / email — dépôt manuel sur le portail, ou pas encore dématérialisé.' },
                { k: 'helios', label: 'Hélios / DGFiP (recommandé)', desc: 'VPC produit les données ; Hélios convertit en Factur-X et dépose sur Chorus Pro.' },
                { k: 'chorus_direct', label: 'Chorus Pro direct', desc: 'VPC génère le Factur-X et dialogue avec l\'API Chorus Pro (raccordement avancé).' },
              ] as const).map((opt) => (
                <label key={opt.k} className="flex items-start gap-3 rounded-r-lg border border-border bg-surface/40 px-3 py-2">
                  <input type="radio" name="mode-efacture" className="mt-1" checked={modeEfacture === opt.k}
                    onChange={() => setModeEfacture(opt.k)} />
                  <span>
                    <span className="text-sm font-medium text-ink">{opt.label}</span>
                    <span className="block text-xs text-ink-subtle">{opt.desc}</span>
                  </span>
                </label>
              ))}
            </div>

            {/* Identifiants selon le mode choisi — stockés chiffrés, mock tant
                que le connecteur n'est pas activé côté serveur. */}
            {modeEfacture === 'helios' && (
              <div className="mt-3 grid grid-cols-1 gap-3 rounded-r-lg border border-border/60 bg-surface/20 p-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="helios-code" className="mb-1 block text-xs font-medium text-ink-muted">Code collectivité (PES/ASAP)</label>
                  <Input id="helios-code" value={heliosCode} onChange={(e) => setHeliosCode(e.target.value)} placeholder="Ex. 017333" />
                </div>
                <div>
                  <label htmlFor="helios-poste" className="mb-1 block text-xs font-medium text-ink-muted">Poste comptable (IdPost, 7 chiffres)</label>
                  <Input id="helios-poste" value={heliosIdPoste} onChange={(e) => setHeliosIdPoste(e.target.value)} placeholder="fourni par la trésorerie" />
                </div>
                <div>
                  <label htmlFor="helios-url" className="mb-1 block text-xs font-medium text-ink-muted">Dépôt PES (URL / SFTP)</label>
                  <Input id="helios-url" value={heliosDepotUrl} onChange={(e) => setHeliosDepotUrl(e.target.value)} placeholder="fourni par la trésorerie" />
                </div>
                <p className="text-xs text-ink-subtle sm:col-span-2">
                  Tant que le connecteur Hélios n'est pas activé, le flux PES est généré et le dépôt simulé (mode test).
                </p>
              </div>
            )}
            {modeEfacture === 'chorus_direct' && (
              <div className="mt-3 grid grid-cols-1 gap-3 rounded-r-lg border border-border/60 bg-surface/20 p-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="cpro-struct" className="mb-1 block text-xs font-medium text-ink-muted">Identifiant structure Chorus Pro</label>
                  <Input id="cpro-struct" value={chorusStructureId} onChange={(e) => setChorusStructureId(e.target.value)} placeholder="SIRET / service" />
                </div>
                <div>
                  <label htmlFor="cpro-login" className="mb-1 block text-xs font-medium text-ink-muted">Compte technique — identifiant</label>
                  <Input id="cpro-login" value={chorusTechLogin} onChange={(e) => setChorusTechLogin(e.target.value)} placeholder="login technique AIFE" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="cpro-pwd" className="mb-1 block text-xs font-medium text-ink-muted">
                    Compte technique — mot de passe {tenant?.chorusTechPasswordConfigure ? '(déjà configuré)' : ''}
                  </label>
                  <Input id="cpro-pwd" type="password" value={chorusTechPassword} onChange={(e) => setChorusTechPassword(e.target.value)}
                    placeholder={tenant?.chorusTechPasswordConfigure ? '•••••••• (laisser vide pour conserver)' : 'mot de passe technique'} />
                </div>
                <p className="text-xs text-ink-subtle sm:col-span-2">
                  Le Factur-X est généré côté VPC. Tant que le connecteur Chorus Pro n'est pas activé, le dépôt est simulé (mode test). Le mot de passe est chiffré et jamais réaffiché.
                </p>
              </div>
            )}

            {/* FranceConnect — identité citoyenne (par commune). */}
            <div className="mt-5 border-t border-border pt-4">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-ink">
                Identité FranceConnect
              </div>
              <p className="mb-3 text-xs text-ink-muted">
                Permet aux administrés de se connecter via FranceConnect (référendums, quotient familial…).
                Les identifiants sont délivrés à la commune par l'habilitation DataPass. Chaque commune saisit ses propres identifiants (il n'y a pas de configuration partagée).
              </p>
              <div className="grid grid-cols-1 gap-3 rounded-r-lg border border-border/60 bg-surface/20 p-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="fc-client-id" className="mb-1 block text-xs font-medium text-ink-muted">Client ID FranceConnect</label>
                  <Input id="fc-client-id" value={fcClientId} onChange={(e) => setFcClientId(e.target.value)} placeholder="client_id de l'habilitation" />
                </div>
                <div>
                  <label htmlFor="fc-client-secret" className="mb-1 block text-xs font-medium text-ink-muted">
                    Client Secret {tenant?.fcClientSecretConfigure ? '(déjà configuré)' : ''}
                  </label>
                  <Input id="fc-client-secret" type="password" value={fcClientSecret} onChange={(e) => setFcClientSecret(e.target.value)}
                    placeholder={tenant?.fcClientSecretConfigure ? '•••••••• (laisser vide pour conserver)' : 'coller le secret FranceConnect'} />
                </div>
                <p className="text-xs text-ink-subtle sm:col-span-2">
                  Le secret est chiffré au repos et jamais réaffiché. Tant que FranceConnect n'est pas activé côté serveur, la connexion reste en mode test.
                </p>
              </div>
            </div>

            {/* API Particulier / Entreprise — jetons par commune. */}
            <div className="mt-5 border-t border-border pt-4">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-ink">
                Données administrés & associations (API Particulier / Entreprise)
              </div>
              <p className="mb-3 text-xs text-ink-muted">
                Jetons délivrés à la commune par ses habilitations DataPass (quotient familial CAF/MSA,
                données associations). Chaque commune saisit ses propres jetons (il n'y a pas de jeton
                partagé). Le SIRET de la commune sert d'identifiant (recipient) — renseignez-le plus haut.
              </p>
              <div className="grid grid-cols-1 gap-3 rounded-r-lg border border-border/60 bg-surface/20 p-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="api-part-token" className="mb-1 block text-xs font-medium text-ink-muted">
                    Jeton API Particulier {tenant?.apiParticulierTokenConfigure ? '(déjà configuré)' : ''}
                  </label>
                  <Input id="api-part-token" type="password" value={apiParticulierToken} onChange={(e) => setApiParticulierToken(e.target.value)}
                    placeholder={tenant?.apiParticulierTokenConfigure ? '•••••••• (laisser vide pour conserver)' : 'jeton API Particulier de la commune'} />
                </div>
                <div>
                  <label htmlFor="api-entr-token" className="mb-1 block text-xs font-medium text-ink-muted">
                    Jeton API Entreprise {tenant?.apiEntrepriseTokenConfigure ? '(déjà configuré)' : ''}
                  </label>
                  <Input id="api-entr-token" type="password" value={apiEntrepriseToken} onChange={(e) => setApiEntrepriseToken(e.target.value)}
                    placeholder={tenant?.apiEntrepriseTokenConfigure ? '•••••••• (laisser vide pour conserver)' : 'jeton API Entreprise de la commune'} />
                </div>
                <p className="text-xs text-ink-subtle sm:col-span-2">
                  Les jetons sont chiffrés au repos et jamais réaffichés. Ces API sont réservées aux acteurs
                  publics : chaque commune obtient ses propres jetons via DataPass.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end border-t border-border pt-3">
            <Button variant="primary" disabled={saving} onClick={() => void enregistrer()}>
              <Save className="h-4 w-4" aria-hidden="true" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}


/**
 * DiffusionPorteeCard — qui peut s'adresser à TOUS les agents (mig 254, 5.25).
 *
 * Par défaut un manager diffuse dans son secteur. Mais « dans une petite mairie,
 * un manager peut avoir un truc important à envoyer sans passer par le maire » :
 * la commune ouvre donc brique par brique, en connaissance de cause.
 *
 * `rh` et `grc` arrivent ouvertes : ce sont les deux fonctions dont le métier
 * EST de s'adresser à l'ensemble du personnel.
 */
/** Réutilisée dans l'onglet « Réglages » de sa brique (refonte UX, chantier 1). */
export function DiffusionPorteeCard() {
  const [reglages, setReglages] = useState<ReglagePorteeDiffusion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const LIBELLES: Record<string, string> = {
    grc: 'Relation citoyen (GRC)', service_technique: 'Service technique',
    scolaire_cantine: 'Scolaire & cantine', petite_enfance: 'Petite enfance',
    associations: 'Associations', urbanisme: 'Urbanisme',
    comptable: 'Comptabilité', rh: 'Ressources humaines',
  };

  const charger = useCallback(async () => {
    setLoading(true);
    try { setReglages(await api.diffusions.reglagesPortee()); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Chargement impossible.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  async function basculer(brique: string, portee: 'secteur' | 'tous') {
    setSaving(brique); setError(null); setSuccess(null);
    try {
      await api.diffusions.setReglagePortee(brique, portee);
      setReglages((prev) => prev.map((r) => (r.brique === brique ? { ...r, portee, parDefaut: false } : r)));
      setSuccess('Réglage enregistré.');
      setTimeout(() => setSuccess(null), 2500);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible.');
    } finally { setSaving(null); }
  }

  return (
    <Card variant="glass" padded className="mt-6">
      <h2 className="font-display text-base font-semibold text-ink">Diffusions internes</h2>
      <p className="mt-1 text-sm text-ink-muted">
        Quelles briques peuvent adresser une diffusion à <strong>tous les agents</strong> de
        la commune, et non seulement à leur propre secteur ? Vous et vos adjoints
        le pouvez toujours.
      </p>

      {error && <Alert variant="error" className="mt-3">{error}</Alert>}
      {success && <Alert variant="success" className="mt-3">{success}</Alert>}

      {loading ? (
        <p className="mt-3 text-sm text-ink-subtle">Chargement…</p>
      ) : (
        <ul className="mt-4 divide-y divide-border">
          {reglages.map((r) => (
            <li key={r.brique} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
              <span className="text-sm text-ink">
                {LIBELLES[r.brique] ?? r.brique}
                {r.parDefaut && <span className="ml-2 text-xs text-ink-subtle">(valeur par défaut)</span>}
              </span>
              <div className="flex gap-1.5">
                {(['secteur', 'tous'] as const).map((p) => (
                  <button
                    key={p} type="button" disabled={saving === r.brique}
                    onClick={() => void basculer(r.brique, p)}
                    aria-pressed={r.portee === p}
                    className={`h-8 rounded-r-pill border px-3 text-xs transition-colors disabled:opacity-50 ${
                      r.portee === p
                        ? 'border-grc/50 bg-grc/10 font-medium text-ink'
                        : 'border-border bg-surface/50 text-ink-muted hover:border-border-strong'
                    }`}
                  >
                    {p === 'secteur' ? 'Son secteur' : 'Tous les agents'}
                  </button>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}


/**
 * SignataireCard — le signataire par défaut et la juridiction (mig 258).
 *
 * Trois champs saisis une fois, qui alimentent {{maire_nom}}, {{maire_titre}} et
 * {{tribunal_administratif}} — trois des quatre variables qui reviennent sur
 * les 150 modèles d'actes. Sans eux, le formulaire « Nouvel arrêté » redemande
 * le nom du maire à chaque acte, avec le risque d'orthographe variable d'un
 * document qui fait foi à l'autre.
 */
function SignataireCard() {
  const [maireNom, setMaireNom] = useState('');
  const [maireTitre, setMaireTitre] = useState('');
  const [tribunal, setTribunal] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const i = await api.admin.tenant.identite.get();
        setMaireNom(i.maireNom ?? '');
        setMaireTitre(i.maireTitre ?? '');
        setTribunal(i.tribunalAdministratif ?? '');
      } catch (e) {
        setError(e instanceof ApiException ? e.message : 'Chargement impossible.');
      } finally { setLoading(false); }
    })();
  }, []);

  async function enregistrer() {
    setSaving(true); setError(null); setSuccess(null);
    try {
      await api.admin.tenant.identite.setSignataire({
        maireNom, maireTitre, tribunalAdministratif: tribunal,
      });
      setSuccess('Enregistré.');
      setTimeout(() => setSuccess(null), 2500);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible.');
    } finally { setSaving(false); }
  }

  return (
    <Card variant="glass" padded className="mt-6">
      <h2 className="font-display text-base font-semibold text-ink">Signataire des actes</h2>
      <p className="mt-1 text-sm text-ink-muted">
        Renseignés ici une fois, ces éléments remplissent automatiquement vos arrêtés.
        Les actes déjà signés gardent le nom de qui les a signés : une élection ne
        réécrit pas le passé.
      </p>

      {error && <Alert variant="error" className="mt-3">{error}</Alert>}
      {success && <Alert variant="success" className="mt-3">{success}</Alert>}

      {loading ? (
        <p className="mt-3 text-sm text-ink-subtle">Chargement…</p>
      ) : (
        <>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Nom du maire" htmlFor="sig-nom">
              <Input id="sig-nom" value={maireNom} onChange={(e) => setMaireNom(e.target.value)}
                placeholder="Jean-Claude MARTIN" />
            </Field>
            <Field label="Titre" htmlFor="sig-titre"
              hint="Tel qu'il figurera au pied des actes">
              <Input id="sig-titre" value={maireTitre} onChange={(e) => setMaireTitre(e.target.value)}
                placeholder="Le Maire" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Tribunal administratif compétent" htmlFor="sig-ta"
                hint="Repris dans la formule de recours de tous vos arrêtés">
                <Input id="sig-ta" value={tribunal} onChange={(e) => setTribunal(e.target.value)}
                  placeholder="Poitiers" />
              </Field>
            </div>
          </div>
          <div className="mt-4">
            <Button variant="primary" size="sm" loading={saving} onClick={() => void enregistrer()}>
              Enregistrer
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

// ============================================================================
// Fond d'écran du tableau de bord (mig 294, demande Henrico 20/08)
//
// Trois fonds livrés (mer / prairie / montagne — « prairie » par défaut si la
// commune ne choisit rien), ou l'image de la commune : compressée CÔTÉ CLIENT
// par `compressImageIfNeeded` (canvas, même helper que les justificatifs)
// avant l'envoi vers R2 — on n'expédie jamais 8 Mo de photo de drone.
// Le tableau de bord pose un voile aux couleurs du thème par-dessus.
// ============================================================================

const FONDS_LIVRES = [
  { choix: 'mer' as const, label: 'Mer' },
  { choix: 'prairie' as const, label: 'Prairie' },
  { choix: 'montagne' as const, label: 'Montagne' },
];

function FondEcranCard() {
  const [fond, setFond] = useState<FondEcran | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const fondRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.admin.tenant.fondEcran().then(setFond).catch(() => {});
  }, []);

  async function agir(fn: () => Promise<FondEcran>, msg: string) {
    setBusy(true); setErr(null); setOk(null);
    try {
      const f = await fn();
      setFond(f); setOk(msg);
      // Valeur fraîche poussée aux couches montées : le fond change PARTOUT
      // à l'instant, sans F5 ni requête supplémentaire.
      invaliderFondCommune(f);
    }
    catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Action impossible'); }
    finally { setBusy(false); }
  }

  async function televerser(file: File) {
    // Compression client (≤ ~1920 px, JPEG) AVANT l'envoi — demande explicite.
    const compresse = await compressImageIfNeeded(file);
    await agir(() => api.admin.tenant.identite.uploadFondEcran(compresse, file.name), 'Image installée comme fond.');
  }

  const vignette = (actif: boolean) =>
    `relative block overflow-hidden rounded-r-lg border-2 transition ${
      actif ? 'border-tech ring-2 ring-tech/40' : 'border-border hover:border-ink-dim'
    }`;

  return (
    <Card variant="glass" padded className="mt-4">
      <SectionEyebrow as="div">Fond d'écran du tableau de bord</SectionEyebrow>
      <p className="mt-1 text-sm text-ink-muted">
        L'image s'affiche derrière les tuiles, sous un voile clair (ou sombre en
        mode nuit) qui garde tout lisible. Trois fonds sont livrés ; vous pouvez
        aussi installer une photo de votre commune.
      </p>
      {err && <Alert variant="error" className="mt-3">{err}</Alert>}
      {ok && <Alert variant="success" className="mt-3">{ok}</Alert>}

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {FONDS_LIVRES.map((f) => (
          <button key={f.choix} type="button" disabled={busy}
            onClick={() => void agir(() => api.admin.tenant.identite.setFondEcran(f.choix), `Fond « ${f.label} » choisi.`)}
            className={vignette(fond?.choix === f.choix)}>
            <img src={`/fonds/${f.choix}.jpg`} alt="" className="h-20 w-full object-cover" />
            <span className="block bg-surface/90 py-1 text-center text-xs font-medium text-ink">{f.label}</span>
          </button>
        ))}
        <button type="button" disabled={busy}
          onClick={() => void agir(() => api.admin.tenant.identite.setFondEcran('aucun'), 'Fond retiré.')}
          className={vignette(fond?.choix === 'aucun')}>
          <span className="flex h-20 w-full items-center justify-center bg-bg text-xs text-ink-dim">Aucun fond</span>
          <span className="block bg-surface/90 py-1 text-center text-xs font-medium text-ink">Aucun</span>
        </button>
      </div>

      {/* Curseur du voile (mig 296) : la mairie règle « plus ou moins clair ».
          Le libellé suit en direct ; l'enregistrement part au lâcher du
          curseur (pointer/clavier), pas à chaque pixel. */}
      {fond && fond.choix !== 'aucun' && (
        <div className="mt-4">
          <label htmlFor="fond-voile" className="mb-1 block text-xs font-medium text-ink-muted">
            Voile : {fond.voile} % — {fond.voile >= 80 ? 'image discrète' : fond.voile >= 60 ? 'équilibré' : 'image très présente'}
          </label>
          <input id="fond-voile" type="range" min={40} max={95} step={5}
            value={fond.voile} disabled={busy}
            onChange={(e) => setFond({ ...fond, voile: Number(e.target.value) })}
            onPointerUp={(e) => void agir(() => api.admin.tenant.identite.setFondVoile(Number((e.target as HTMLInputElement).value)), 'Voile réglé.')}
            onKeyUp={(e) => { if (e.key.startsWith('Arrow')) void agir(() => api.admin.tenant.identite.setFondVoile(Number((e.target as HTMLInputElement).value)), 'Voile réglé.'); }}
            className="w-full max-w-md accent-[oklch(var(--c-tech))]" />
          <p className="mt-1 text-xs text-ink-dim">Vers la gauche : la photo ressort. Vers la droite : presque uni, le plus lisible.</p>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <input ref={fondRef} type="file" accept="image/jpeg,image/png" className="hidden" aria-label="Choisir un fond d’écran personnalisé"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void televerser(f); e.target.value = ''; }} />
        <Button variant="secondary" disabled={busy} onClick={() => fondRef.current?.click()}>
          <Upload className="h-4 w-4" /> {busy ? 'Envoi…' : 'Ma propre image'}
        </Button>
        <span className="text-xs text-ink-dim">
          1920 × 1080 conseillé · JPEG ou PNG · compressée automatiquement avant l'envoi.
        </span>
        {fond?.choix === 'perso' && fond.url && (
          <span className={vignette(true) + ' h-12 w-20'}>
            <img src={fond.url} alt="Fond personnalisé" className="h-full w-full object-cover" />
          </span>
        )}
      </div>
    </Card>
  );
}
